import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import FloatingAIChat from '../../components/ui/FloatingAIChat';
import CourseHero from './components/CourseHero';
import CourseTabs from './components/CourseTabs';
import LessonCard from './components/LessonCard';
import ActivityCard from './components/ActivityCard';
import CourseOverview from './components/CourseOverview';
import HighlightCard from './components/HighlightCard';
import CourseSidebar from './components/CourseSidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useCourseWithModulesAndLessons, useLessons, useModuleBySlug } from '../../hooks/useCourseData';
import { getInstructorForCourse } from '../../data/instructors';
import { useAuth } from '../../contexts/AuthContext';
import { isLessonUnlockedForNavigation } from '@/lib/courseAccess';
import { useCourseAccess } from '../../hooks/useCourseAccess';
import { useUserProgress } from '../../hooks/useUserProgress';


const CourseDetailView = () => {
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const [activeTab, setActiveTab] = useState('lessons');
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [searchParams] = useSearchParams();
  const params = useParams();
  
  // Get module slug from URL params (new route) or query params (legacy)
  const moduleSlug = params.moduleSlug || searchParams.get('module');
  const courseSlug = params.courseSlug || 'formacao-lideres-mentores';
  
  // CourseDetailView agora é usado apenas para visualização do curso, não para módulos
  
  // Use Supabase data if available
  const { tenantId, course, modules, loading: courseLoading } = useCourseWithModulesAndLessons(courseSlug);
  const { module } = useModuleBySlug(tenantId, course?.id, moduleSlug);
  
  // Get lessons for the specific module OR for the first module if no specific module
  const targetModuleId = module?.id || (modules && modules.length > 0 ? modules[0].id : null);
  const effectiveModuleSlug = module?.slug || (modules && modules.length > 0 ? modules[0].slug : undefined);
  const { lessons, loading: lessonsLoading } = useLessons(targetModuleId);

  const courseIdString = course?.id ? String(course.id) : undefined;
  const {
    isFullCourseEnrolled,
    isModuleAccessible,
  } = useCourseAccess(session?.systemUser?.id, courseIdString);
  const { progress: courseProgress, loading: progressLoading, refresh: refreshCourseProgress } = useUserProgress(
    session?.systemUser?.id,
    user?.id,
    courseIdString
  );

  useEffect(() => {
    const onFocus = () => refreshCourseProgress();
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshCourseProgress();
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshCourseProgress]);

  const totalCourseLessons = useMemo(
    () => modules?.reduce((sum, mod) => sum + (mod.lessons?.length ?? 0), 0) ?? 0,
    [modules]
  );

  const userProgress = useMemo(() => {
    if (!courseProgress) return null;
    return {
      completedLessons: courseProgress.completedLessonIds,
      moduleProgress: courseProgress.moduleProgress,
      progressPercentage: courseProgress.progressPercentage,
      totalLessons: courseProgress.totalLessons,
    };
  }, [courseProgress]);

  const tenantRole = session?.userTenant?.role;

  const transformedLessons = useMemo(() => {
    if (!lessons?.length) return [];
    const moduleIdForLesson = targetModuleId ? String(targetModuleId) : undefined;
    const enrolledInModule = moduleIdForLesson
      ? isModuleAccessible(moduleIdForLesson)
      : false;

    return lessons.map((lesson, index) => ({
      id: lesson.id,
      title: lesson.title,
      description: lesson.description || '',
      thumbnail: lesson.video_url ? '/assets/images/major-high-logo.png' : '/assets/images/no_image.png',
      duration: lesson.duration_seconds ?? null,
      isLocked: !isLessonUnlockedForNavigation({
        lessonIsFree: !!lesson.is_free,
        userTenantRole: tenantRole,
        isEnrolledInCourse: isFullCourseEnrolled,
        isEnrolledInModule: enrolledInModule,
      }),
      order: lesson.order || index + 1,
    }));
  }, [lessons, tenantRole, isFullCourseEnrolled, isModuleAccessible, targetModuleId]);

  // Course data will come from Supabase
  const courseData = {
    id: course?.id || "1a1f5462-fa9c-4e94-a067-2cdd7168a8d4", // UUID
    slug: course?.slug || courseSlug, // CRÍTICO: Adicionar slug para navegação
    title: course?.title || "Carregando...",
    category: "Desenvolvimento",
    description: course?.summary || "Carregando descrição do curso...",
    fullDescription: course?.summary || "Carregando descrição completa...",
    thumbnail: course?.cover || "/assets/images/no_image.png",
    duration: null,
    studentsCount: null,
    rating: 0,
    lessonsCount: totalCourseLessons,
    certificateAvailable: false,
    instructor: getInstructorForCourse(course?.instructor),
    learningObjectives: [
      "Fundamentos práticos para construir lideranca, posicionamento e mentorias de alto nivel de entrega e impacto.",
    ],
    requirements: [
      "Disciplina, Foco e Persistencia para construir grandes resultados",
    ],
    modules: modules || []
  };

  // userProgress vem de useUserProgress (por estudante logado)

  // Dados mock removidos - agora usando apenas dados reais do Supabase
  const mockActivities = [];
  const mockHighlights = [];

  const breadcrumbItems = [
    { label: 'Início', path: '/dashboard-home' },
    { label: 'Cursos', path: '/courses' },
    { label: courseData?.title, path: `/course/${courseSlug}`, isActive: true }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
              case 'lessons':
          return (
            <div className="space-y-6">
              {lessonsLoading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="bg-muted rounded-lg p-6 animate-pulse">
                      <div className="h-4 bg-muted-foreground/20 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-muted-foreground/20 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              ) : transformedLessons.length > 0 ? (
                transformedLessons.map((lesson, index) => (
                  <LessonCard
                    key={lesson?.id}
                    lesson={lesson}
                    isCompleted={userProgress?.completedLessons?.includes(String(lesson?.id))}
                    isLocked={lesson.isLocked}
                    progress={userProgress?.completedLessons?.includes(String(lesson?.id)) ? 100 : 0}
                    courseSlug={courseSlug}
                    moduleSlug={effectiveModuleSlug}
                  />
                ))
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="BookOpen" size={24} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Nenhuma lição disponível</h3>
                  <p className="text-muted-foreground mb-4">
                    Este módulo ainda não possui lições cadastradas.
                  </p>
                  <Button onClick={() => navigate('/dashboard-home')}>
                    Voltar ao Início
                  </Button>
                </div>
              )}
            </div>
          );

      case 'highlights':
        return (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Nenhum destaque disponível no momento.</p>
          </div>
        );

      case 'activities':
        return (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Nenhuma atividade disponível no momento.</p>
          </div>
        );

      case 'overview':
        return <CourseOverview course={courseData} />;

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Breadcrumb customItems={breadcrumbItems} />
          
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <div className="flex-1">
              <CourseHero
                course={courseData}
                userProgress={userProgress}
                courseProgress={courseProgress}
                progressLoading={progressLoading}
                modules={modules}
                tenantId={tenantId}
              />
              
              {/* Mobile Sidebar Toggle */}
              <div className="lg:hidden mb-6">
                <Button
                  variant="default"
                  onClick={() => setSidebarVisible(true)}
                  iconName="List"
                  iconPosition="left"
                  fullWidth
                >
                  Ver Módulos do Curso
                </Button>
              </div>

              <CourseTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                lessonCount={transformedLessons.length}
                activityCount={mockActivities?.length}
              />

              {renderTabContent()}
            </div>

            {/* Desktop Sidebar */}
            <div className="lg:w-80">
              <CourseSidebar
                course={courseData}
                userProgress={userProgress}
                isVisible={sidebarVisible}
                onClose={() => setSidebarVisible(false)}
                courseSlug={courseSlug}
                userTenantRole={tenantRole}
                isEnrolledInCourse={isFullCourseEnrolled}
                isModuleAccessible={isModuleAccessible}
              />
            </div>
          </div>
        </div>
      </main>
      <FloatingAIChat />
    </div>
  );
};

export default CourseDetailView;
